/**
 * Single entry point. Routes by query string:
 *   ?embedded=1 (or the transient ?embed=1) → embedded app with a real robot
 *   ?demo=1                                 → "Try on screen" demo mode
 *   otherwise                               → landing page ("Connect my Reachy" → SDK host shell)
 *
 * SDK bootstrapping (adapted from tfrere/minimal-conversation src/dispatch.ts, MIT):
 * the host shell and the embed client both wait for `window.ReachyMini`, so the
 * constructor is assigned synchronously and `reachymini:ready` dispatched
 * BEFORE either host module is imported.
 */
import { ReachyMini } from "@pollen-robotics/reachy-mini-sdk";
import "./ui/styles.css";
import { APP_EMOJI, APP_ICON_URL, APP_NAME, devClientId, devToken } from "./app/hf";

window.ReachyMini = ReachyMini;
window.dispatchEvent(new Event("reachymini:ready"));

const params = new URLSearchParams(window.location.search);
const isEmbed = params.get("embedded") === "1" || params.get("embed") === "1";
const isDemo = params.get("demo") === "1";

function appRoot(): HTMLElement {
  let root = document.getElementById("app");
  if (!root) {
    root = document.createElement("div");
    root.id = "app";
    document.body.append(root);
  }
  return root;
}

function fail(err: unknown): void {
  console.error("[main] failed to load:", err);
  const root = appRoot();
  root.textContent = "Reachy couldn't load. Please check your connection and reload the page.";
  root.hidden = false;
}

/** Hand over to the SDK host shell (HF sign-in, robot picker, iframe with ?embedded=1). */
async function connectMyReachy(root: HTMLElement): Promise<void> {
  const { mountHost } = await import("@pollen-robotics/reachy-mini-sdk/host/auto");
  root.replaceChildren();
  root.hidden = true;
  const dev = devToken();
  mountHost({
    appName: APP_NAME,
    appIconUrl: APP_ICON_URL,
    appEmoji: APP_EMOJI,
    enableMicrophone: false, // Reachy listens with the robot's microphone
    clientId: devClientId(),
    devToken: dev,
  });
}

const root = appRoot();
if (isEmbed) {
  import("./app/embed").then((m) => m.bootEmbed(root)).catch(fail);
} else if (isDemo) {
  import("./app/demo").then((m) => m.bootDemo(root)).catch(fail);
} else {
  import("./app/landing")
    .then((m) =>
      m.showLanding(root, {
        onConnect: () => void connectMyReachy(root).catch(fail),
        onTryOnScreen: () => {
          const next = new URLSearchParams(window.location.search);
          next.set("demo", "1");
          window.location.search = next.toString();
        },
      }),
    )
    .catch(fail);
}
